import type { ChecklistDetail } from "@/react/components/entityViews/checklist/utils/checklistApiAdapters";
import type {
  CheckGridAttention,
  CheckGridCellHistoryEntry,
  CheckGridDefinition,
  CheckGridDisplayState,
  CheckGridMatrix,
  CheckGridMatrixCell,
  CheckGridOverviewItem,
  CheckGridRecurrence,
  CheckGridSubject,
  CheckGridSubjectType,
  CheckGridSummary,
  CheckGridTemplateItem
} from "../types";
import { UNASSIGNED_SUBJECT_ID } from "../types";

export type StatusMatrixPayload = {
  available?: boolean;
  checklist_id: number;
  recurrence?: CheckGridRecurrence | string;
  recurrence_weekdays?: number[] | null;
  from?: string | null;
  to?: string | null;
  assignees?: { id: number; name: string }[];
  items?: { id: number; description: string; order: number; archived?: boolean }[];
  cells?: {
    checklist_item_id: number;
    board_user_id: number;
    status: number;
    checked: boolean;
    recorded_at: string | null;
    recorded_by_id?: number | null;
    display_state?: string;
    date?: string | null;
  }[];
};

export type StatusLogRow = {
  id: number;
  checklist_item_id: number;
  board_user_id: number;
  board_user_name?: string;
  status: number;
  checked: boolean;
  recorded_at: string;
  /** Account id of who made the change — null on rows logged before this was recorded. */
  recorded_by_id?: number | null;
};

export const emptySummary = (partial?: Partial<CheckGridSummary>): CheckGridSummary => ({
  subject_count: 0,
  item_count: 0,
  applicable: 0,
  unresolved: 0,
  missed: 0,
  attention_needed: 0,
  last_activity: null,
  ...partial
});

export function subjectTypeFromEntityIds(row: { task_id?: number | null; client_id?: number | null; resource_id?: number | null }): CheckGridSubjectType {
  if (row.task_id) return "task";
  if (row.client_id) return "client";
  return "resource";
}

/** Map API `display_state` (+ checked fallback) → UI cell state. */
export function mapDisplayState(raw: string | undefined, checked: boolean): CheckGridDisplayState {
  switch (raw) {
    case "done":
      return "satisfied";
    case "not_required":
      return "not_applicable";
    case "missed":
      return "missed";
    case "expected":
      return "expected";
    case "incomplete":
      return "incomplete";
    default:
      if (checked) return "satisfied";
      return "incomplete";
  }
}

function mapAttention(displayState: CheckGridDisplayState): CheckGridAttention {
  return displayState === "missed" ? "attention_needed" : "normal";
}

export function matrixFromStatusMatrix(payload: StatusMatrixPayload, checklistId: number, overview?: CheckGridOverviewItem | null): CheckGridMatrix {
  const assignees = payload.assignees ?? [];
  const items = (payload.items ?? []).filter((item) => !item.archived);
  const subjects: CheckGridSubject[] = assignees.map((a) => ({
    subject_id: a.id,
    checklist_id: checklistId,
    name: a.name,
    resource_id: overview?.resource_id ?? null,
    task_id: overview?.task_id ?? null,
    client_id: overview?.client_id ?? null
  }));

  const templateItems: CheckGridTemplateItem[] = items.map((item) => ({
    id: item.id,
    key: String(item.id),
    description: item.description,
    order: item.order,
    archived: item.archived
  }));

  const liveItemIds = new Set(templateItems.map((i) => i.id));
  const cells: CheckGridMatrixCell[] = (payload.cells ?? [])
    .filter((cell) => liveItemIds.has(cell.checklist_item_id))
    .map((cell) => {
      const display_state = mapDisplayState(cell.display_state, cell.checked);
      return {
        subject_id: cell.board_user_id,
        item_id: cell.checklist_item_id,
        checklist_id: checklistId,
        checklist_item_id: cell.checklist_item_id,
        recorded_at: cell.recorded_at,
        recorded_by_id: cell.recorded_by_id ?? null,
        applicability: display_state === "not_applicable" ? "not_applicable" : "applicable",
        satisfaction: display_state === "satisfied" ? "satisfied" : "pending",
        checked: display_state === "satisfied",
        display_state,
        attention: mapAttention(display_state),
        has_evidence: false
      };
    });

  return finalizeMatrix({
    checklist_id: checklistId,
    available: true,
    from: payload.from ?? "",
    to: payload.to ?? "",
    as_of: payload.to ?? payload.from ?? new Date().toISOString(),
    phase: "current",
    subjects,
    items: templateItems,
    cells,
    summary: emptySummary()
  });
}

type RetrieveItemRow = {
  id: number;
  description?: string;
  order?: number;
  archived?: boolean;
  status?: number;
  checked?: boolean;
  checked_by?: string | null;
  checked_date?: string | null;
  comment?: string | null;
  image_url?: string | null;
  thumbnail_url?: string | null;
};

export type RetrieveForMatrix = {
  assigned_boardusers?: Array<number | string>;
  assigned_boarduser_ids?: number[];
  items?: RetrieveItemRow[];
  title?: string;
  checklist_title?: string;
  recurrence?: CheckGridRecurrence | string;
  recurrence_weekdays?: number[] | null;
  archived?: boolean;
  can_view_grant?: number;
  can_edit_grant?: number;
  resource?: number | null;
  task?: number | null;
  client?: number | null;
};

function cellFromChecked(
  checklistId: number,
  subjectId: number,
  itemId: number,
  checked: boolean,
  recordedAt: string | null = null,
  checkedBy: string | null = null
): CheckGridMatrixCell {
  const display_state = mapDisplayState(checked ? "done" : "incomplete", checked);
  return {
    subject_id: subjectId,
    item_id: itemId,
    checklist_id: checklistId,
    checklist_item_id: itemId,
    recorded_at: recordedAt,
    checked_by: checkedBy,
    applicability: "applicable",
    satisfaction: display_state === "satisfied" ? "satisfied" : "pending",
    checked: display_state === "satisfied",
    display_state,
    attention: mapAttention(display_state),
    has_evidence: false
  };
}

function finalizeMatrix(matrix: CheckGridMatrix): CheckGridMatrix {
  const due = matrix.cells.filter((c) => c.display_state === "incomplete" || c.display_state === "missed");
  const missed = matrix.cells.filter((c) => c.display_state === "missed").length;
  const applicable = matrix.cells.filter(
    (c) => c.display_state === "incomplete" || c.display_state === "missed" || c.display_state === "satisfied"
  ).length;
  return {
    ...matrix,
    available: true,
    summary: emptySummary({
      subject_count: matrix.subjects.length,
      item_count: matrix.items.length,
      applicable,
      unresolved: due.length,
      missed,
      attention_needed: missed,
      last_activity: matrix.cells.map((c) => c.recorded_at).filter(Boolean).sort().at(-1) ?? null
    })
  };
}

export type EnrichMatrixOptions = {
  /**
   * When true (default period / Today only), empty cells take checked state from retrieve.
   * History windows must leave this false — retrieve is current-period caller state, not that day.
   */
  fillCheckedFromRetrieve?: boolean;
};

/**
 * When the status-matrix omits cells for 0–1 assignees (or returns available:false),
 * fill subjects/cells from retrieve so the check grid still renders.
 */
export function enrichMatrixFromRetrieve(
  matrix: CheckGridMatrix,
  retrieve: RetrieveForMatrix | null | undefined,
  nameByBoardUserId?: (id: number) => string | undefined,
  options?: EnrichMatrixOptions
): CheckGridMatrix {
  const fillCheckedFromRetrieve = options?.fillCheckedFromRetrieve !== false;
  const checklistId = matrix.checklist_id;
  let subjects = matrix.subjects;
  let items = matrix.items;
  let cells = matrix.cells;

  if (!items.length && retrieve?.items?.length) {
    items = retrieve.items
      .filter((item) => !item.archived)
      .map((item, index) => ({
        id: item.id,
        key: String(item.id),
        description: item.description ?? "",
        order: item.order ?? index,
        archived: Boolean(item.archived)
      }));
  }

  // Comment and photo are item-level (not period state), so they apply to history windows too.
  const detailsById = new Map((retrieve?.items ?? []).map((item) => [item.id, item]));
  items = items.map((item) => {
    const detail = detailsById.get(item.id);
    return detail
      ? { ...item, comment: detail.comment ?? null, image_url: detail.image_url ?? null, thumbnail_url: detail.thumbnail_url ?? null }
      : item;
  });

  if (!subjects.length) {
    const fromIds = Array.isArray(retrieve?.assigned_boarduser_ids) ? retrieve.assigned_boarduser_ids : [];
    const fromAssigned = Array.isArray(retrieve?.assigned_boardusers)
      ? retrieve.assigned_boardusers.filter((v): v is number => typeof v === "number")
      : [];
    const ids = fromIds.length ? fromIds : fromAssigned;
    if (ids.length) {
      subjects = ids.map((id) => ({
        subject_id: id,
        checklist_id: checklistId,
        name: nameByBoardUserId?.(id) ?? `User ${id}`,
        resource_id: null,
        task_id: null,
        client_id: null
      }));
    } else {
      subjects = [
        {
          subject_id: UNASSIGNED_SUBJECT_ID,
          checklist_id: checklistId,
          name: "Unassigned",
          resource_id: null,
          task_id: null,
          client_id: null
        }
      ];
    }
  }

  if (!cells.length && items.length && subjects.length) {
    const retrieveById = new Map((retrieve?.items ?? []).map((item) => [item.id, item]));
    cells = subjects.flatMap((subject) =>
      items.map((item) => {
        const row = retrieveById.get(item.id);
        const checked =
          fillCheckedFromRetrieve && (row?.checked === true || row?.status === 2);
        return cellFromChecked(
          checklistId,
          subject.subject_id,
          item.id,
          checked,
          fillCheckedFromRetrieve ? row?.checked_date ?? null : null,
          fillCheckedFromRetrieve ? row?.checked_by ?? null : null
        );
      })
    );
  }

  cells = cells.map((cell) =>
    cell.checked_by || cell.recorded_by_id == null || cell.display_state !== "satisfied"
      ? cell
      : { ...cell, checked_by: nameByBoardUserId?.(cell.recorded_by_id) ?? null }
  );

  return finalizeMatrix({ ...matrix, subjects, items, cells });
}

export function definitionFromMatrixAndOverview(
  overview: CheckGridOverviewItem,
  matrix: CheckGridMatrix,
  grants?: { can_view_grant?: number; can_edit_grant?: number; archived?: boolean }
): CheckGridDefinition {
  return {
    id: overview.id,
    name: overview.name,
    subject_type: overview.subject_type,
    recurrence: overview.recurrence,
    recurrence_weekdays: overview.recurrence_weekdays,
    archived: grants?.archived ?? overview.archived,
    can_view_grant: grants?.can_view_grant ?? overview.can_view_grant,
    can_edit_grant: grants?.can_edit_grant ?? overview.can_edit_grant,
    resource_id: overview.resource_id ?? null,
    task_id: overview.task_id ?? null,
    client_id: overview.client_id ?? null,
    items: matrix.items,
    subjects: matrix.subjects
  };
}

/** `actorName` resolves `recorded_by_id` (an account id) to a name; rows without one fall back to the cell's owner. */
export function historyFromStatusLog(
  results: StatusLogRow[],
  actorName: (userId: number) => string | undefined
): CheckGridCellHistoryEntry[] {
  return results.map((row) => ({
    id: row.id,
    recorded_at: row.recorded_at,
    checked: row.checked,
    applicability: "applicable" as const,
    comment: null,
    comment_by: null,
    checked_by: (row.recorded_by_id != null ? actorName(row.recorded_by_id) : undefined) ?? row.board_user_name ?? null,
    has_evidence: false
  }));
}

/** Adapt shared EM detail cache into the matrix enricher shape. */
export function retrieveForMatrixFromDetail(detail: ChecklistDetail | undefined): RetrieveForMatrix | undefined {
  if (!detail) return undefined;
  const { checklist, items } = detail;
  return {
    title: checklist.title,
    checklist_title: checklist.title,
    recurrence: checklist.recurrence,
    recurrence_weekdays: checklist.recurrenceWeekdays ?? null,
    archived: checklist.archived,
    can_view_grant: checklist.canView,
    can_edit_grant: checklist.canEdit,
    resource: checklist.resourceId != null ? Number(checklist.resourceId) : null,
    task: checklist.taskId != null ? Number(checklist.taskId) : null,
    client: checklist.clientId != null ? Number(checklist.clientId) : null,
    assigned_boardusers: checklist.assignedBoardUsers,
    assigned_boarduser_ids: checklist.assignedBoardUsers,
    items: items.map((item) => ({
      id: Number(item.id),
      description: item.description,
      order: item.order,
      archived: item.archived,
      status: item.status,
      checked: item.checked,
      checked_by: item.checkedBy ?? null,
      checked_date: item.checkedDate != null ? String(item.checkedDate) : null,
      comment: item.comment ?? null,
      image_url: item.image ?? null,
      thumbnail_url: item.thumbnailUrl ?? null
    }))
  };
}
